import { emptyTableEntry, isInstanceOf, isRootIdKey, isTypeOrUndefined, isVertexKey, } from "./aux";
import { PartState, isAssembly, isManufacturablePart, isNotManufacturablePart, isPartState, isPurchasedPart, isUserDefinedTube, } from "./documentdialog";
import { createMaterialString, } from "./material";
import { isArray, isString, } from "./wsi4_typeguards";
export function isSearchRequest(arg) {
    const searchRequestTypeGuard = {
        searchText: isString,
    };
    return isInstanceOf(arg, searchRequestTypeGuard);
}
export var CheckedState;
(function (CheckedState) {
    CheckedState["none"] = "none";
    CheckedState["all"] = "all";
    CheckedState["partial"] = "partial";
})(CheckedState || (CheckedState = {}));
export function isCheckedState(c) {
    return c === CheckedState.none
        || c === CheckedState.all
        || c === CheckedState.partial;
}
export function checkedState(arr) {
    return arr.every(c => c === CheckedState.none) ? CheckedState.none : (arr.every(c => c === CheckedState.all) ? CheckedState.all : CheckedState.partial);
}
export function isRowDisplayInformation(arg) {
    const rowDisplayInformationTypeGuardMap = {
        articleName: isString,
        checkedState: isCheckedState,
        materialWithCuttingGas: isString,
        multiplicity: isString,
        partState: (arg) => isTypeOrUndefined(arg, isPartState),
        png: (arg) => isTypeOrUndefined(arg, isString),
        price: isString,
        rootIdKey: isRootIdKey,
        svg: (arg) => isTypeOrUndefined(arg, isString),
        thickness: isString,
        unitPrice: isString,
        vertexKey: isVertexKey,
    };
    return isInstanceOf(arg, rowDisplayInformationTypeGuardMap);
}
export function isDisplayAssembly(arg) {
    const displayAssemblyTypeGuardMap = {
        assembly: isRowDisplayInformation,
        parts: (t) => isArray(t, isRowDisplayInformation),
    };
    return isInstanceOf(arg, displayAssemblyTypeGuardMap);
}
export function isDisplayProject(arg) {
    const displayProjectTypeGuardMap = {
        singleParts: (t) => isArray(t, isRowDisplayInformation),
        assemblies: (t) => isArray(t, isDisplayAssembly),
    };
    return isInstanceOf(arg, displayProjectTypeGuardMap);
}
export function formatPrice(price, priceFactors, i18n) {
    return typeof price === "number" && price > 0 ? i18n.currencyString(price, priceFactors) : emptyTableEntry;
}
export function getRowDisplayInformation(projectEntry, priceFactors, i18n, shopConfig, selection) {
    if (projectEntry === undefined) {
        throw Error("Wrong type.");
    }
    const thicknessStr = (() => {
        if (isManufacturablePart(projectEntry)) {
            return i18n.numberWithTwoDecimals(projectEntry.thickness) + " mm";
        }
        if (isUserDefinedTube(projectEntry)) {
            return i18n.numberWithTwoDecimals(projectEntry.geometry.content.thickness) + " mm";
        }
        return emptyTableEntry;
    })();
    const partState = (() => {
        if (isManufacturablePart(projectEntry)) {
            return projectEntry.partState;
        }
        else if (isAssembly(projectEntry)) {
            return undefined;
        }
        else if (isPurchasedPart(projectEntry)) {
            return PartState.purchasedPart;
        }
        else if (isUserDefinedTube(projectEntry)) {
            return projectEntry.partState;
        }
        else if (isNotManufacturablePart(projectEntry)) {
            return projectEntry.partState;
        }
        throw Error("Wrong type.");
    })();
    const materialStr = (() => {
        if (isManufacturablePart(projectEntry)) {
            const globalMaterial = shopConfig.material.find(g => projectEntry.materialIdWithCuttingGasId.sheetMaterialId === g.identifier);
            if (globalMaterial === undefined) {
                throw Error("Missing material.");
            }
            return createMaterialString(globalMaterial.name, projectEntry.materialIdWithCuttingGasId.cuttingGasId, i18n);
        }
        if (isUserDefinedTube(projectEntry)) {
            return projectEntry.material === undefined ? "" : projectEntry.material.name;
        }
        return emptyTableEntry;
    })();
    const price = (() => {
        if (isManufacturablePart(projectEntry)) {
            return projectEntry.approxPrice;
        }
        if (isAssembly(projectEntry)) {
            return projectEntry.approxPrice;
        }
        if (isPurchasedPart(projectEntry)) {
            return projectEntry.price;
        }
        if (isUserDefinedTube(projectEntry)) {
            return projectEntry.approxPrice;
        }
        if (isNotManufacturablePart(projectEntry)) {
            return projectEntry.approxPrice;
        }
        throw Error("Wrong type.");
    })();
    const checkedState = (() => {
        if (isAssembly(projectEntry)) {
            const selected = projectEntry.parts.filter(p => selection.some(s => s === p.rootIdKey));
            return selected.length === projectEntry.parts.length ? CheckedState.all : selected.length === 0 ? CheckedState.none : CheckedState.partial;
        }
        return selection.some(s => s === projectEntry.rootIdKey) ? CheckedState.all : CheckedState.none;
    })();
    return {
        articleName: projectEntry.articleName,
        checkedState: checkedState,
        materialWithCuttingGas: materialStr,
        multiplicity: projectEntry.multiplicity.toFixed(0),
        partState: partState,
        png: projectEntry.png,
        price: formatPrice(price, priceFactors, i18n),
        rootIdKey: projectEntry.rootIdKey,
        svg: projectEntry.svg,
        thickness: thicknessStr,
        unitPrice: formatPrice(price === undefined ? undefined : price / projectEntry.multiplicity, priceFactors, i18n),
        vertexKey: projectEntry.vertexKey,
    };
}
export function createDisplayProject(project, priceFactors, i18n, shopConfig, selection) {
    return {
        singleParts: project.singleParts.map(s => getRowDisplayInformation(s, priceFactors, i18n, shopConfig, selection)),
        assemblies: project.assemblies.map(a => ({
            assembly: getRowDisplayInformation(a, priceFactors, i18n, shopConfig, selection),
            parts: a.parts.map(p => getRowDisplayInformation(p, priceFactors, i18n, shopConfig, selection)),
        })),
    };
}
function matchesArticleNameFilter(filterText, t) {
    if (filterText.length === 0) {
        return true;
    }
    const lcs = filterText.toLowerCase();
    return t.articleName.toLowerCase()
        .indexOf(lcs) !== -1;
}
function filterByArticleNameMatches(filterText, array) {
    if (filterText.length === 0) {
        return array;
    }
    return array
        .reduce((res, entry) => {
        if (matchesArticleNameFilter(filterText, entry)) {
            res.push(entry);
        }
        return res;
    }, []);
}
export function filterDisplayProject(project, searchText, selection) {
    return {
        singleParts: filterByArticleNameMatches(searchText, project.singleParts),
        assemblies: project.assemblies
            .map(a => {
            if (matchesArticleNameFilter(searchText, a.assembly)) {
                return a;
            }
            else {
                const parts = filterByArticleNameMatches(searchText, a.parts);
                const checkedState = (() => {
                    const selected = parts.filter(p => selection.some(s => s === p.rootIdKey));
                    return selected.length === parts.length ? CheckedState.all : selected.length === 0 ? CheckedState.none : CheckedState.partial;
                })();
                return {
                    ...a,
                    assembly: {
                        ...a.assembly,
                        checkedState: checkedState,
                    },
                    parts: parts,
                };
            }
        })
            .filter(a => a.parts.length !== 0),
    };
}
export var Column;
(function (Column) {
    Column["checkbox"] = "checkbox";
    Column["collapsedState"] = "collapsedState";
    Column["thumbnailAssembly"] = "thumbnailAssembly";
    Column["thumbnailPart"] = "thumbnailPart";
    Column["partState"] = "partState";
    Column["articleName"] = "articleName";
    Column["multiplicity"] = "multiplicity";
    Column["materialWithCuttingGas"] = "materialWithCuttingGas";
    Column["thickness"] = "thickness";
    Column["unitPrice"] = "unitPrice";
    Column["price"] = "price";
})(Column || (Column = {}));
export const orderedColumnNames = [
    Column.checkbox,
    Column.collapsedState,
    Column.thumbnailAssembly,
    Column.thumbnailPart,
    Column.partState,
    Column.articleName,
    Column.multiplicity,
    Column.materialWithCuttingGas,
    Column.thickness,
    Column.unitPrice,
    Column.price,
];
const numberColumns = [
    Column.multiplicity,
    Column.thickness,
    Column.unitPrice,
    Column.price,
];
function typeCmp(lhs, rhs) {
    const type = (elem) => {
        switch (elem.partState) {
            case PartState.notManufacturable: return 0;
            case PartState.manufacturableWithWarning: return 1;
            case PartState.sheetCuttingPart: return 2;
            case PartState.sheetBendingPart: return 3;
            case PartState.userDefinedTube: return 4;
            case PartState.purchasedPart: return 5;
            case undefined: return 6;
        }
    };
    return Math.max(Math.min(type(lhs) - type(rhs), 1), -1);
}
function numberStringCmp(lhs, rhs) {
    return lhs.localeCompare(rhs, undefined, { numeric: true });
}
function stringCmp(lhs, rhs) {
    return lhs.localeCompare(rhs);
}
function namedColumnCmpImpl(lhsEntry, rhsEntry, columnName) {
    if (lhsEntry === emptyTableEntry) {
        if (rhsEntry === emptyTableEntry) {
            return 0.;
        }
        return 1.;
    }
    if (rhsEntry === emptyTableEntry) {
        return -1.;
    }
    if (numberColumns.some(c => c === columnName)) {
        return numberStringCmp(lhsEntry, rhsEntry);
    }
    return stringCmp(lhsEntry, rhsEntry);
}
function namedColumnCmp(lhs, rhs, columnName) {
    const lhsEntry = lhs[columnName];
    const rhsEntry = rhs[columnName];
    if (!isString(lhsEntry)) {
        throw Error("Wrong type");
    }
    if (!isString(rhsEntry)) {
        throw Error("Wrong type");
    }
    return namedColumnCmpImpl(lhsEntry, rhsEntry, columnName);
}
function sortPredicateForRowDisplayInformations(rowDisplayA, rowDisplayB, sortedColumn) {
    let cmp = 0;
    if (sortedColumn.columnName === Column.partState) {
        cmp = typeCmp(rowDisplayA, rowDisplayB);
    }
    else {
        cmp = namedColumnCmp(rowDisplayA, rowDisplayB, sortedColumn.columnName);
    }
    if (cmp !== 0) {
        cmp = cmp * (sortedColumn.up ? 1. : -1.);
        return cmp;
    }
    return namedColumnCmp(rowDisplayA, rowDisplayB, Column.articleName) * (sortedColumn.up ? 1. : -1.);
}
export function sortDisplayProject(project, sortedColumn) {
    return {
        singleParts: project.singleParts.sort((a, b) => sortPredicateForRowDisplayInformations(a, b, sortedColumn)),
        assemblies: project.assemblies
            .map(a => ({
            assembly: a.assembly,
            parts: a.parts.sort((a, b) => sortPredicateForRowDisplayInformations(a, b, sortedColumn)),
        }))
            .sort((a, b) => sortPredicateForRowDisplayInformations(a.assembly, b.assembly, sortedColumn)),
    };
}
export function articleName(displayInfo, searchText) {
    if (searchText.length === 0 || !matchesArticleNameFilter(searchText, displayInfo)) {
        return displayInfo.articleName;
    }
    const lcs = searchText.toLowerCase();
    const index = displayInfo.articleName.toLowerCase()
        .indexOf(lcs);
    if (index === -1) {
        throw Error("Wrong implementation.");
    }
    const front = displayInfo.articleName.slice(0, index);
    const highlight = searchText;
    const tail = displayInfo.articleName.slice(index + searchText.length);
    return {
        front: front,
        highlight: highlight,
        tail: tail,
    };
}
